import React, { useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { FaFolder, FaSitemap, FaArrowRight, FaEdit } from "react-icons/fa";
import { MdHistory } from "react-icons/md";

const COLORS = {
  orange: "#f97316",
  orangeLight: "#fff7ed",
  blue: "#3b82f6",
  blueLight: "#eff6ff",
  gray: "#6b7280",
  grayLight: "#f3f4f6",
};

const getTypeStyle = (moveType) => {
  switch (moveType) {
    case "space":
      return { bg: COLORS.orangeLight, color: COLORS.orange };
    case "rename":
      return { bg: COLORS.blueLight, color: COLORS.blue };
    default:
      return { bg: COLORS.grayLight, color: COLORS.gray };
  }
};

const getTypeIcon = (moveType) => {
  switch (moveType) {
    case "space":
      return <FaSitemap className="text-sm" style={{ color: COLORS.orange }} />;
    case "rename":
      return <FaEdit className="text-sm" style={{ color: COLORS.blue }} />;
    default:
      return <FaFolder className="text-sm" style={{ color: COLORS.gray }} />;
  }
};

export default function MoveHistoryPanel({ onClose }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchHistory();
  }, [page]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("userToken");
      const response = await fetch(
        `${process.env.REACT_APP_BE_MAIN_URL}/ocr/move-history/?page=${page}&page_size=20`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch move history");
      }

      const data = await response.json();
      setRecords(data.records || []);
      setTotalPages(Math.ceil((data.total || 0) / 20));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncatePath = (path, maxLength = 30) => {
    if (path.length <= maxLength) return path;
    return "..." + path.slice(-maxLength + 3);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-white shadow-xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: COLORS.orangeLight }}
            >
              <MdHistory className="text-xl" style={{ color: COLORS.orange }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">History</h2>
              <p className="text-sm text-gray-500">Recent moves and renames</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <IoClose className="text-xl text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 mb-2">{error}</p>
              <button
                onClick={fetchHistory}
                className="text-orange-500 hover:underline text-sm"
              >
                Try again
              </button>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12">
              <MdHistory className="text-4xl text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No history yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Use arrows to move or the edit button to rename
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {records.map((record) => (
                <div
                  key={record.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                >
                  {/* Header with icon and name */}
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: getTypeStyle(record.move_type).bg }}
                    >
                      {getTypeIcon(record.move_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">
                          {record.item_name}
                        </span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: getTypeStyle(record.move_type).bg,
                            color: getTypeStyle(record.move_type).color,
                          }}
                        >
                          {record.move_type}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        by {record.moved_by} on {formatDate(record.moved_at)}
                      </p>
                    </div>
                  </div>

                  {/* Path change */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className="text-gray-600 truncate flex-1"
                        title={record.old_path}
                      >
                        {truncatePath(record.old_path)}
                      </span>
                      <FaArrowRight className="text-gray-400 flex-shrink-0" />
                      <span
                        className="text-gray-900 font-medium truncate flex-1"
                        title={record.new_path}
                      >
                        {truncatePath(record.new_path)}
                      </span>
                    </div>
                  </div>

                  {/* Stats */}
                  {(record.affected?.children > 0 ||
                    record.affected?.folders > 0 ||
                    record.affected?.files > 0 ||
                    record.affected_files > 0) && (
                    <div className="flex gap-4 mt-3 text-xs text-gray-500">
                      {record.affected?.children > 0 && (
                        <span>{record.affected.children} child spaces</span>
                      )}
                      {record.affected?.folders > 0 && (
                        <span>{record.affected.folders} folders</span>
                      )}
                      {(record.affected?.files > 0 || record.affected_files > 0) && (
                        <span>
                          {record.affected?.files || record.affected_files} files
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
